import { SearchBrandModalRender, type BrandItem } from "./search-brand-modal-render";
import { ModalSearchBase } from "../../../components/modal-search/modal-search-base-handler";
import { Actions } from "../../../constants/actions";
import { SearchBrandModalListener } from "./search-brand-modal-listener";
import type { PhpBrandData } from "../../../types/php-injected";

type UnknownRecord = Record<string, unknown>;

type SearchBrandModalSourceItem = unknown | PhpBrandData;

function isRecord(value: unknown): value is UnknownRecord {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

function toBrandItem(raw: SearchBrandModalSourceItem): BrandItem | null {
    const obj = isRecord(raw) ? raw : null;
    if (!obj) return null;

    const brandId = String(obj["brand_id"] ?? "").trim();
    const brandName = String(obj["brand_name"] ?? "").trim();
    if (!brandId || !brandName) return null;
    const brandIndex = typeof obj["brand_index"] === "string" ? String(obj["brand_index"]).trim() : "";

    const countRaw =
        ("products_count" in obj ? obj["products_count"] : undefined) ??
        ("productsCount" in obj ? obj["productsCount"] : undefined);
    const count = typeof countRaw === "number" ? countRaw : countRaw != null ? Number(countRaw) : null;
    const productsCount = Number.isFinite(count) ? Math.max(0, Math.trunc(count)) : null;

    return { brandId, brandName, brandIndex, productsCount };
}

function isBrandItem(item: BrandItem | null): item is BrandItem {
    return item !== null;
}

export interface SearchBrandModalHandlerParams {
    overlayEl: HTMLElement;
    modalEl: HTMLElement;
    popularRootEl: HTMLElement;
    allRootEl: HTMLElement;
    filterInputEl: HTMLInputElement | null;
    showAllToggleEl?: HTMLInputElement | null;
}

export interface SearchBrandModalData {
    popularBrands?: SearchBrandModalSourceItem[];
    allBrands?: SearchBrandModalSourceItem[];
}

export class SearchBrandModalHandler {
    public popularRootEl: HTMLElement;
    public allRootEl: HTMLElement;
    public showAll: boolean;
    public showAllToggleEl: HTMLInputElement | null;
    public popular: BrandItem[];
    public all: BrandItem[];
    public base: ModalSearchBase;

    public constructor(params: SearchBrandModalHandlerParams) {
        this.popularRootEl = params.popularRootEl;
        this.allRootEl = params.allRootEl;
        this.showAll = this.createShowAllFlag();
        this.showAllToggleEl = this.createShowAllToggleSlot(params.showAllToggleEl);

        if (this.showAllToggleEl) {
            this.showAllToggleEl.checked = this.showAll;
            SearchBrandModalListener.bindShowAllToggle(this.showAllToggleEl, () => {
                this.showAll = !!this.showAllToggleEl?.checked;
                this.base.render();
            });
        }

        this.popular = this.createBrandItemsSlot();
        this.all = this.createBrandItemsSlot();

        this.base = new ModalSearchBase({
            overlayEl: params.overlayEl,
            modalEl: params.modalEl,
            filterInputEl: params.filterInputEl,
            adapter: {
                render: (base) => {
                    SearchBrandModalRender.render({
                        popularRoot: this.popularRootEl,
                        allRoot: this.allRootEl,
                        popular: this.popular,
                        all: this.all,
                        selectedIds: base.selectedIds,
                        filterText: base.query,
                        showAll: this.showAll,
                    });
                },
                handleAction: (base, action, actionEl) => {
                    if (action !== Actions.SELECT_BRAND) return false;
                    const id = String(actionEl.getAttribute("data-brand-id") || "").trim();
                    if (!id) return true;
                    base.toggleSelectedId(id);
                    base.render();
                    return true;
                },
            },
        });
        this.base.setSelectionMode("multi");
        this.base.setCloseOnSelect(false);
    }

    public createShowAllFlag(): boolean {
        return false;
    }

    public createShowAllToggleSlot(value: Element | null | undefined): HTMLInputElement | null {
        return value instanceof HTMLInputElement ? value : null;
    }

    public createBrandItemsSlot(): BrandItem[] {
        return [];
    }

    public setData(data: SearchBrandModalData): void {
        const obj = isRecord(data) ? data : {};

        const popularRaw = Array.isArray(obj.popularBrands) ? obj.popularBrands : [];
        const allRaw = Array.isArray(obj.allBrands) ? obj.allBrands : [];

        this.popular = popularRaw.map(toBrandItem).filter(isBrandItem);
        this.all = allRaw.map(toBrandItem).filter(isBrandItem);

        if (this.all.length === 0 && this.popular.length > 0) {
            this.all = [...this.popular];
        }
    }

    public setSelectedIds(ids: string[]): void {
        this.base.setSelectedIds(ids);
    }

    public getSelectedIds(): string[] {
        return this.base.getSelectedIds();
    }

    public setOnSelectionChange(callback: (ids: string[]) => void): void {
        this.base.setOnSelectionChange(callback);
    }

    public render(): void {
        this.base.render();
    }

    public open(): void {
        this.base.open();
    }

    public close(): void {
        this.base.close();
    }

    public handleOverlayClick(event: MouseEvent): void {
        this.base.handleOverlayClick(event);
    }

    public handleCloseClick(event: MouseEvent): void {
        this.base.handleCloseClick(event);
    }

    public handleFilterInput(event: InputEvent): void {
        this.base.handleFilterInput(event);
    }

    public handleListClick(event: MouseEvent): void {
        this.base.handleActionClick(event);
    }

    public get isOpen(): boolean {
        return this.base.isOpen;
    }

    public getBase(): ModalSearchBase {
        return this.base;
    }
}
